import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaNewspaper, 
  FaEye, 
  FaChartLine, 
  FaCheckCircle, 
  FaClock, 
  FaEdit,
  FaArrowUp,
  FaArrowDown,
  FaChartBar,
  FaPlus,
  FaList,
  FaTags,
  FaSync,
  FaDownload,
  FaExclamationTriangle,
  FaThumbsUp,
  FaShare,
  FaCalendarAlt,
  FaFire,
  FaBolt,
  FaArrowRight
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';
import { 
  getNewsDashboardStats,
  getSiteVisitTrends
} from '../../../../firebase/firestoreService';
import { useUI } from '../../../../components/common/UIMessageProvider';

// Local types for News Dashboard
interface TopCategory { category: string; count: number }
interface MonthlyStat { month: string; published: number; views: number }
interface TrendingArticle { id: string; title: string; views: number; publishedAt: string }
interface NewsDashboardStats {
  totalNews: number;
  publishedNews: number;
  draftNews: number;
  scheduledNews: number;
  totalViews: number;
  averageViews: number;
  topCategories: TopCategory[];
  trendingNews: TrendingArticle[];
  monthlyStats: MonthlyStat[];
  carouselCount: number;
  cardsCount: number;
  generalCount: number;
}
type SiteVisitTrend = { period: string; visits: number };

// Stat Card for News Dashboard only
interface NewsStatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<any>;
  trend?: { value: number; isPositive: boolean };
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'gray';
  description?: string;
}

const NewsStatCard: React.FC<NewsStatCardProps> = ({
  title,
  value,
  icon: Icon,
  trend,
  color,
  description
}) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    green: 'bg-green-100 text-green-600 border-green-200',
    purple: 'bg-purple-100 text-purple-600 border-purple-200',
    orange: 'bg-orange-100 text-orange-600 border-orange-200',
    red: 'bg-red-100 text-red-600 border-red-200',
    gray: 'bg-gray-100 text-gray-600 border-gray-200'
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-6 border">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          
          {trend && (
            <div className="flex items-center mt-2">
              <span className={`text-sm font-medium ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {trend.isPositive ? '+' : ''}{trend.value}%
              </span>
              <span className="text-sm text-gray-500 ml-2">from last period</span>
            </div>
          )}
          
          {description && (
            <p className="text-xs text-gray-500 mt-2">{description}</p>
          )}
        </div>
        
        <div className={`h-12 w-12 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
};

const NewsDashboard: React.FC = () => {
  const ui = useUI();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<NewsDashboardStats | null>(null);
  const [trends, setTrends] = useState<SiteVisitTrend[]>([]);
  const [trendPeriod, setTrendPeriod] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');

  const visitsChartData = useMemo(() => {
    if (trendPeriod === 'weekly') {
      // Keep the weekly chart continuous by always rendering all 8 expected weeks.
      const expectedWeeks = Array.from({ length: 8 }, (_, index) => `Week ${index + 1}`);
      const trendMap = new Map(trends.map((trend) => [trend.period, trend]));

      return expectedWeeks.map((period) => {
        const trend = trendMap.get(period);
        return {
          period,
          visits: trend?.visits || 0
        };
      });
    }

    return trends.map((trend) => ({
      period: trend.period,
      visits: trend.visits || 0
    }));
  }, [trends, trendPeriod]);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Get dashboard stats
      const statsResult = await getNewsDashboardStats();
      if (statsResult.success && statsResult.data) {
        // Ensure missing optional fields are present
        const safeStats: NewsDashboardStats = {
          totalNews: statsResult.data.totalNews || 0,
          publishedNews: (statsResult.data as any).publishedNews ?? 0,
          draftNews: (statsResult.data as any).draftNews ?? 0,
          scheduledNews: statsResult.data.scheduledNews || 0,
          totalViews: statsResult.data.totalViews || 0,
          averageViews: statsResult.data.averageViews || 0,
          topCategories: statsResult.data.topCategories || [],
          trendingNews: statsResult.data.trendingNews || [],
          monthlyStats: statsResult.data.monthlyStats || [],
          carouselCount: statsResult.data.carouselCount || 0,
          cardsCount: statsResult.data.cardsCount || 0,
          generalCount: statsResult.data.generalCount || 0,
        };
        setStats(safeStats);
      } else {
        ui.showMessage('Failed to load news statistics', { type: 'error' });
      }

      // Get trends
      const trendsResult = await getSiteVisitTrends(trendPeriod);
      if (trendsResult.success && trendsResult.data) {
        setTrends(trendsResult.data);
      }
    } catch (error: any) {
      ui.showMessage('Error loading dashboard data', { type: 'error' });
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [trendPeriod]);

  // Quick actions
  const quickActions = [
    {
      title: 'Create News',
      description: 'Write a new article',
      icon: FaPlus,
      color: 'bg-blue-500',
      link: '/admin/news/create'
    },
    {
      title: 'Manage News',
      description: 'View all articles',
      icon: FaList,
      color: 'bg-green-500',
      link: '/admin/news/list'
    },
    {
      title: 'Carousel Items',
      description: 'Manage homepage carousel',
      icon: FaChartBar,
      color: 'bg-purple-500',
      link: '/admin/news/carousel'
    },
    {
      title: 'Categories',
      description: 'Manage categories',
      icon: FaTags,
      color: 'bg-orange-500',
      link: '/admin/news/categories'
    }
  ];

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">News Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Overview of news articles, views, and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={trendPeriod}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTrendPeriod(e.target.value as 'weekly' | 'monthly' | 'yearly')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="weekly">Weekly Trends</option>
            <option value="monthly">Monthly Trends</option>
            <option value="yearly">Yearly Trends</option>
          </select>
          <button
            onClick={fetchDashboardData}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <FaSync className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <NewsStatCard
          title="Total Articles"
          value={stats?.totalNews || 0}
          icon={FaNewspaper}
          trend={{ value: 12, isPositive: true }}
          color="blue"
          description="All news articles"
        />
        <NewsStatCard
          title="Published"
          value={stats?.publishedNews || 0}
          icon={FaCheckCircle}
          trend={{ value: 8, isPositive: true }}
          color="green"
          description="Currently live"
        />
        <NewsStatCard
          title="Total Views"
          value={stats?.totalViews?.toLocaleString() || '0'}
          icon={FaEye}
          trend={{ value: 18, isPositive: true }}
          color="purple"
          description="All-time views"
        />
        <NewsStatCard
          title="Avg. Views"
          value={stats?.averageViews?.toLocaleString() || '0'}
          icon={FaChartLine}
          trend={{ value: 5, isPositive: true }}
          color="orange"
          description="Per article"
        />
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-soft p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Carousel Items</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.carouselCount || 0}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FaChartBar className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Featured on homepage
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-soft p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Card Items</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.cardsCount || 0}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
              <FaNewspaper className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Displayed in cards section
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-soft p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">General News</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.generalCount || 0}</p>
            </div>
            <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <FaList className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Available in news section
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Categories Chart */}
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">News Categories</h3>
              <p className="text-sm text-gray-600">Distribution by category</p>
            </div>
            <FaTags className="h-6 w-6 text-primary-600" />
          </div>
          {stats?.topCategories && stats.topCategories.length > 0 ? (
            <div className="space-y-3">
              {stats.topCategories.map((category: TopCategory, index: number) => (
                <div key={category.category} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-lg bg-primary-100 text-primary-800 flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-900">{category.category}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">{category.count} articles</span>
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary-600 h-2 rounded-full"
                        style={{ 
                          width: `${(category.count / (stats.totalNews || 1)) * 100}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FaExclamationTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p>No category data available</p>
            </div>
          )}
        </div>

        {/* Site Visits Trend Chart */}
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Site Visits Trend</h3>
              <p className="text-sm text-gray-600">
                {trendPeriod === 'weekly'
                  ? 'Continuous weekly visits (no missing weeks)'
                  : 'Total website visits'}
              </p>
            </div>
            <FaChartLine className="h-6 w-6 text-primary-600" />
          </div>
          {visitsChartData.length > 0 ? (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={visitsChartData} margin={{ top: 8, right: 16, left: -12, bottom: 8 }}>
                  <defs>
                    <linearGradient id="visitsAreaFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#4f46e5" stopOpacity={0.06} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <Tooltip
                    formatter={(value: any, name: any) => {
                      const numericValue =
                        typeof value === 'number' ? value : Number(value) || 0;
                      return [
                        numericValue.toLocaleString(),
                        name === 'visits' ? 'Site Visits' : 'Visits'
                      ];
                    }}
                    labelStyle={{ color: '#111827' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="visits"
                    name="visits"
                    stroke="#4f46e5"
                    fill="url(#visitsAreaFill)"
                    strokeWidth={3}
                    dot={false}
                    connectNulls
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FaExclamationTriangle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p>No trend data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trending News */}
        <div className="bg-white rounded-xl shadow-soft p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Trending Articles</h3>
              <p className="text-sm text-gray-600">Most viewed articles</p>
            </div>
            <FaFire className="h-5 w-5 text-red-500" />
          </div>
          <div className="space-y-4">
            {stats?.trendingNews && stats.trendingNews.length > 0 ? (
              stats.trendingNews.map((article: TrendingArticle, index: number) => (
                <div key={article.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-lg bg-primary-100 text-primary-800 flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 group-hover:text-primary-700">
                        {article.title}
                      </h4>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-sm text-gray-500">
                          {formatDate(article.publishedAt)}
                        </span>
                        <span className="text-sm text-gray-500">•</span>
                        <span className="text-sm text-gray-500 flex items-center">
                          <FaEye className="h-3 w-3 mr-1" />
                          {article.views.toLocaleString()} views
                        </span>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/admin/news/edit/${article.id}`}
                    className="text-primary-600 hover:text-primary-700 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <FaEdit className="h-4 w-4" />
                  </Link>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FaExclamationTriangle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p>No trending articles available</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
              <p className="text-sm text-gray-600">Frequent tasks</p>
            </div>
            <FaBolt className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="space-y-3">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.link}
                className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all group"
              >
                <div className={`h-10 w-10 rounded-lg ${action.color} flex items-center justify-center text-white`}>
                  <action.icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 group-hover:text-primary-700">
                    {action.title}
                  </h4>
                  <p className="text-sm text-gray-500">{action.description}</p>
                </div>
                <FaArrowRight className="h-4 w-4 text-gray-400 group-hover:text-primary-600" />
              </Link>
            ))}
          </div>

          {/* Stats Summary */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Draft Articles:</span>
                <span className="font-medium">{stats?.draftNews || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Scheduled:</span>
                <span className="font-medium">{stats?.scheduledNews || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Top Category:</span>
                <span className="font-medium">
                  {stats?.topCategories[0]?.category || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsDashboard;
